import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CLIENTS } from '../mock';
import ClientLogo from './ClientLogo';

/**
 * ClientShowreel — a two-line auto-scrolling marquee of REAL client logos.
 *
 * All logos are rendered at a consistent capped height, greyscale by default
 * and reveal their brand colour on hover. The two rows scroll in opposite
 * directions with soft gradient fades on both edges for a clean fade-in/out.
 */
export default function ClientShowreel() {
  // Split the 21 clients into two visually balanced rows.
  const half = Math.ceil(CLIENTS.length / 2);
  const rowA = CLIENTS.slice(0, half);
  const rowB = CLIENTS.slice(half);

  // Duplicate each row so the marquee loops seamlessly.
  const trackA = [...rowA, ...rowA];
  const trackB = [...rowB, ...rowB];

  return (
    <section className="bg-[#FBFAF6]">
      {/* Impact band */}
      <div className="dwx py-16">
        <div className="rounded-[28px] bg-[#0F3D22] p-10 md:p-16">
          <div className="grid grid-cols-12 gap-10 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0] mb-6">
                Client outcomes
              </div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.98] text-[#FBFAF6]">
                Work that reached
                <br />production.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/80">
                A short walk through the enterprise programmes we have worked on &mdash; what the problem was, what shipped, and where NeuralWorks changed how the work got done.
              </p>
              <Link
                to="/work"
                data-testid="client-showreel-cta"
                className="group mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#22A94F] text-[#0F3D22] text-[14px] font-semibold hover:bg-[#7ED4A0] transition-colors"
              >
                Explore client stories
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Client showreel — real logos, displayed in full colour */}
      <div className="pb-24">
        <div className="dwx text-center mb-16 md:mb-20">
          <div className="mono text-[12px] md:text-[13px] tracking-[0.28em] uppercase text-[#1A1A1A]/70">
            Trusted by enterprise teams at
          </div>
        </div>

        {/* Row A — scrolls left */}
        <MarqueeRow>
          <div className="marquee-track flex items-center gap-8 md:gap-14" style={{ width: 'max-content' }}>
            {trackA.map((c, i) => (
              <LogoCell key={`a-${c.name}-${i}`} client={c} />
            ))}
          </div>
        </MarqueeRow>

        {/* Row B — scrolls right (reverse) */}
        <MarqueeRow className="mt-4">
          <div
            className="flex items-center gap-8 md:gap-14"
            style={{ width: 'max-content', animation: 'marquee-scroll-rev 140s linear infinite' }}
          >
            {trackB.map((c, i) => (
              <LogoCell key={`b-${c.name}-${i}`} client={c} />
            ))}
          </div>
        </MarqueeRow>
      </div>
    </section>
  );
}

function MarqueeRow({ children, className = '' }) {
  return (
    <div className={`relative overflow-hidden py-3 ${className}`}>
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#FBFAF6] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#FBFAF6] to-transparent z-10 pointer-events-none" />
      {children}
    </div>
  );
}

// A single logo cell — larger unframed logo on the ambient cream surface,
// each cell fixed to a consistent block so the marquee spacing stays clean.
function LogoCell({ client }) {
  return (
    <div className="flex items-center justify-center h-24 min-w-[180px] md:min-w-[200px] px-4">
      <ClientLogo logo={client.logo} name={client.name} size="md" />
    </div>
  );
}
